/**
 * The question sets, and the only place they are written down.
 *
 * Both the learner-facing forms and the admin reader import from here, so a
 * stored answer can always be rendered with the wording it was actually asked
 * under. That is what `key` (e.g. "video.teacher.v1") is for: question sets
 * VERSION. Changing a question means minting a new key, never editing an old
 * one in place — otherwise historical answers silently acquire a meaning nobody
 * gave them.
 */

export type FeedbackRole = 'STUDENT' | 'TEACHER' | 'PARENT' | 'ADMIN_STAFF' | 'OTHER';
export type FeedbackSubject = 'VIDEO' | 'ASSESSMENT' | 'TOPIC' | 'FRAMEWORK';

export interface Choice {
  value: string;
  label: string;
}

export interface Question {
  id: string;
  prompt: string;
  /** `scale` renders 1–5; `choice` renders buttons; `multi` allows several. */
  type: 'scale' | 'choice' | 'multi' | 'text';
  choices?: Choice[];
  /** Shown under the prompt when the scale's ends need naming. */
  lowLabel?: string;
  highLabel?: string;
  optional?: boolean;
}

export interface FormSet {
  key: string;
  subject: FeedbackSubject;
  role: FeedbackRole;
  title: string;
  intro: string;
  questions: Question[];
  /** The always-visible free-text box. The text is the payload, so it is never
   *  hidden behind a "tell us more" click. */
  commentPrompt: string;
  /** Extra free-text fields stored in their own columns, not in `answers`. */
  demand?: boolean;
}

const YES_MAYBE_NO: Choice[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'maybe', label: 'Maybe' },
  { value: 'no', label: 'No' },
];

export const VIDEO_STUDENT: FormSet = {
  key: 'video.student.v1',
  subject: 'VIDEO',
  role: 'STUDENT',
  title: 'How was this lesson?',
  intro: 'Five quick questions. It helps us make the next one better.',
  questions: [
    {
      id: 'understanding',
      prompt: 'Do you understand this topic better than before?',
      type: 'scale',
      lowLabel: 'Not really',
      highLabel: 'Much better',
    },
    {
      id: 'pace',
      prompt: 'How was the pace?',
      type: 'choice',
      choices: [
        { value: 'slow', label: 'Too slow' },
        { value: 'right', label: 'Just right' },
        { value: 'fast', label: 'Too fast' },
      ],
    },
    {
      id: 'interaction',
      prompt: 'The moments where you had to answer or try something — did they help you think, or interrupt you?',
      type: 'choice',
      choices: [
        { value: 'helped', label: 'Helped me think' },
        { value: 'neither', label: 'Neither' },
        { value: 'interrupted', label: 'Interrupted me' },
      ],
    },
    {
      id: 'vs_textbook',
      prompt: 'Would you pick this over reading the chapter in your textbook?',
      type: 'choice',
      choices: YES_MAYBE_NO,
    },
    {
      id: 'want_more',
      prompt: 'Would you want your other subjects taught like this?',
      type: 'choice',
      choices: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ],
    },
  ],
  commentPrompt: 'What confused you, if anything?',
};

export const VIDEO_TEACHER: FormSet = {
  key: 'video.teacher.v1',
  subject: 'VIDEO',
  role: 'TEACHER',
  title: 'Your read on this lesson',
  intro: 'Five questions, under a minute. Your judgement decides what we build next.',
  questions: [
    {
      id: 'accuracy',
      prompt: 'Is the content accurate and right for this grade?',
      type: 'scale',
      lowLabel: 'Problems',
      highLabel: 'Sound',
    },
    {
      id: 'would_use',
      prompt: 'Would you use this in class?',
      type: 'choice',
      choices: [
        { value: 'as_is', label: 'Yes, as is' },
        { value: 'with_changes', label: 'Yes, with changes' },
        { value: 'no', label: 'No' },
      ],
    },
    {
      id: 'coverage',
      prompt: 'Does it cover what the chapter actually requires?',
      type: 'choice',
      choices: [
        { value: 'fully', label: 'Fully' },
        { value: 'partly', label: 'Partly' },
        { value: 'no', label: 'No' },
      ],
    },
    {
      id: 'time_saved',
      prompt: 'Where would this save you the most time?',
      type: 'choice',
      choices: [
        { value: 'hard_idea', label: 'Explaining a hard idea' },
        { value: 'practice', label: 'Practice' },
        { value: 'revision', label: 'Revision' },
        { value: 'attention', label: 'Holding attention' },
        { value: 'nowhere', label: 'Nowhere' },
      ],
    },
    {
      id: 'recommend',
      prompt: 'How likely are you to recommend it to another teacher?',
      type: 'scale',
      lowLabel: 'Not at all',
      highLabel: 'Very likely',
    },
  ],
  commentPrompt: 'Anything wrong, misleading, or missing?',
};

export const ASSESSMENT_FORM: FormSet = {
  key: 'assessment.v1',
  subject: 'ASSESSMENT',
  role: 'OTHER', // the form asks for the role up front and overrides this
  title: 'How was this check?',
  intro: 'Five questions about the questions.',
  questions: [
    {
      id: 'difficulty',
      prompt: 'Was the difficulty right for the grade?',
      type: 'choice',
      choices: [
        { value: 'easy', label: 'Too easy' },
        { value: 'right', label: 'Right' },
        { value: 'hard', label: 'Too hard' },
      ],
    },
    {
      id: 'tests_what',
      prompt: 'Do the questions test understanding, or memory?',
      type: 'choice',
      choices: [
        { value: 'understanding', label: 'Understanding' },
        { value: 'mixed', label: 'Mixed' },
        { value: 'memory', label: 'Memory' },
      ],
    },
    {
      id: 'feedback_useful',
      prompt: 'Was the feedback after each answer useful?',
      type: 'scale',
      lowLabel: 'Not useful',
      highLabel: 'Very useful',
    },
    {
      id: 'would_use_as',
      prompt: 'Would you use this as classwork, homework, or a test?',
      type: 'multi',
      choices: [
        { value: 'classwork', label: 'Classwork' },
        { value: 'homework', label: 'Homework' },
        { value: 'test', label: 'A test' },
        { value: 'none', label: 'None of these' },
      ],
    },
    {
      id: 'fair',
      prompt: 'Did the scoring feel fair?',
      type: 'scale',
      lowLabel: 'Unfair',
      highLabel: 'Fair',
    },
  ],
  commentPrompt: 'What would you add or remove?',
};

export const TOPIC_TEACHER: FormSet = {
  key: 'topic.teacher.v1',
  subject: 'TOPIC',
  role: 'TEACHER',
  title: 'What should we build next?',
  intro:
    'You teach this every year and we do not. Tell us where the gaps are — including topics we have not made yet.',
  questions: [
    {
      id: 'struggle',
      prompt: 'Which parts of this chapter do your students struggle with most?',
      type: 'text',
    },
    {
      id: 'teach_today',
      prompt: 'How do you teach this today?',
      type: 'multi',
      choices: [
        { value: 'textbook', label: 'Textbook' },
        { value: 'blackboard', label: 'Blackboard' },
        { value: 'slides', label: 'Slides' },
        { value: 'videos', label: 'Videos' },
        { value: 'lab', label: 'Lab / practical' },
        { value: 'none', label: 'Nothing specific' },
      ],
    },
  ],
  commentPrompt: 'Anything else we should know?',
  demand: true,
};

export const FRAMEWORK_FORM: FormSet = {
  key: 'framework.v1',
  subject: 'FRAMEWORK',
  role: 'OTHER', // role asked up front
  title: 'What do you make of all this?',
  intro: 'Not one lesson — the whole thing. Five questions.',
  questions: [
    {
      id: 'overall',
      prompt: 'Overall, how good is this compared with what you use today?',
      type: 'scale',
      lowLabel: 'Worse',
      highLabel: 'Much better',
    },
    {
      id: 'biggest_value',
      prompt: 'What is the single most valuable part?',
      type: 'choice',
      choices: [
        { value: 'interaction', label: 'The interactivity' },
        { value: 'visuals', label: 'How it looks' },
        { value: 'checks', label: 'The checks' },
        { value: 'tracking', label: 'Progress tracking' },
        { value: 'coverage', label: 'Syllabus coverage' },
      ],
    },
    {
      id: 'blocker',
      prompt: 'What would stop you using this in your school?',
      type: 'choice',
      choices: [
        { value: 'devices', label: 'Devices / internet' },
        { value: 'time', label: 'Class time' },
        { value: 'coverage', label: 'Not enough coverage yet' },
        { value: 'cost', label: 'Cost' },
        { value: 'nothing', label: 'Nothing' },
      ],
    },
    {
      id: 'recommend',
      prompt: 'How likely are you to recommend this to a colleague?',
      type: 'scale',
      lowLabel: 'Not at all',
      highLabel: 'Very likely',
    },
  ],
  commentPrompt: 'Tell us anything — good or bad.',
};

export const ALL_FORMS: FormSet[] = [
  VIDEO_STUDENT,
  VIDEO_TEACHER,
  ASSESSMENT_FORM,
  TOPIC_TEACHER,
  FRAMEWORK_FORM,
];

export function formByKey(key: string): FormSet | undefined {
  return ALL_FORMS.find((f) => f.key === key);
}

/** Renders a stored answer with the wording it was asked under. */
export function describeAnswer(formKey: string, questionId: string, value: unknown): {
  prompt: string;
  display: string;
} {
  const form = formByKey(formKey);
  const q = form?.questions.find((x) => x.id === questionId);
  if (!q) return { prompt: questionId, display: String(value ?? '—') };
  if (q.type === 'multi' && Array.isArray(value)) {
    const labels = value.map((v) => q.choices?.find((c) => c.value === v)?.label ?? String(v));
    return { prompt: q.prompt, display: labels.join(', ') || '—' };
  }
  if (q.type === 'choice') {
    return { prompt: q.prompt, display: q.choices?.find((c) => c.value === value)?.label ?? String(value ?? '—') };
  }
  if (q.type === 'scale') return { prompt: q.prompt, display: value ? `${value} of 5` : '—' };
  return { prompt: q.prompt, display: String(value ?? '—') };
}
